import axios, { AxiosInstance } from "axios";
import { BASE_URL } from "configs/api";
import { INew, INewsRes } from "./types";
import { newsStore } from "./store";
import { $api } from "services";
import { notify } from "hooks/useToast/notify";

class NewsApi {
  private _api: AxiosInstance;

  constructor() {
    this._api = axios.create({
      baseURL: BASE_URL,
      withCredentials: true,
    });
  }

  private handleError(error: any) {
    if (error.response) {
      notify({
        type: "error",
        meassage: error.response.data.message,
      });
    } else {
      notify({
        type: "error",
        meassage: "Something went wrong",
      });
    }
  }

  async getNews(
    page: number = 0,
    take: number = 10,
    refetch?: boolean,
  ): Promise<INewsRes> {
    const url = `/api/news/admin/all`;
    try {
      const { data } = await $api.get<INewsRes>(url, {
        params: { page, limit: take },
      });

      if (refetch) {
        const isThereMore = data.meta.totalPages !== page;

        newsStore.setState({
          news: data.data,
          hasMore: isThereMore,
          page: isThereMore ? page + 1 : page,
        });
      }

      return data;
    } catch (error) {
      this.handleError(error);
      return {
        data: [],
        meta: {
          itemsPerPage: 0,
          totalItems: 0,
          currentPage: 0,
          totalPages: 0,
        },
      };
    }
  }

  async createNew(newsData: INew, callback?: () => void) {
    const url = `/api/news/add`;
    newsStore.setState({ isAddLoading: true });
    const { setNews, news } = newsStore.getState();

    try {
      const payload = {
        title: newsData.title,
        description: newsData.description,
        image: newsData.image,
        link: newsData.link,
      };

      const { data } = await this._api.post<INew>(url, payload);

      if (!!callback) {
        callback();
      }

      setNews([...news, data]);

      notify({
        type: "success",
        meassage: "The new has been successfully created",
      });
    } catch (error) {
      this.handleError(error);
    } finally {
      newsStore.setState({ isAddLoading: false });
    }
  }

  async editNew(newsData: INew, callback?: () => void) {
    const url = `/api/news/edit`;
    newsStore.setState({ isAddLoading: true });
    const { setNews, news } = newsStore.getState();

    try {
      const payload = {
        id: newsData.id,
        title: newsData.title,
        description: newsData.description,
        image: newsData.image,
        link: newsData.link,
      };

      const { data: updatedNew } = await this._api.patch<INew>(url, payload);

      const updatedNews = news.map((newItem) => {
        if (newItem.id === newsData.id) {
          return updatedNew;
        }

        return newItem;
      });

      setNews(updatedNews);

      if (!!callback) {
        callback();
      }

      notify({
        type: "success",
        meassage: "The new has been successfully updated",
      });
    } catch (error) {
      this.handleError(error);
    } finally {
      newsStore.setState({ isAddLoading: false });
    }
  }

  async deleteNew({ newId }: { newId: string }) {
    const { news, setNews } = newsStore.getState();
    const url = `/api/news/${newId}`;

    try {
      await this._api.delete(url);

      const updatedNews = news.filter((newItem) => newItem.id !== newId);
      setNews(updatedNews);

      notify({
        type: "success",
        meassage: "The new has been successfully deleted",
      });
    } catch (error) {
      this.handleError(error);
    }
  }
}

export const newsApi = new NewsApi();
